import { useState, type ReactNode } from "react";

/**
 * Disclosure is a row that opens into its controls.
 *
 * The studio's chapter page had every narration setting standing open above
 * the text, so the first screen of a chapter was a form. A setting is changed
 * once per book and read a hundred times, so the row shows the current value
 * in one line and opens on a tap. Nothing inside is hidden from anyone who
 * looks; it is just not in the way.
 */
export default function Disclosure({
  title,
  summary,
  defaultOpen = false,
  testId,
  toggleTestId,
  className = "",
  children,
}: {
  title: string;
  /** The current state in one line, shown closed and open. */
  summary?: ReactNode;
  defaultOpen?: boolean;
  /** On the wrapper, so a test can scope to the row and its contents. */
  testId?: string;
  toggleTestId?: string;
  className?: string;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div
      data-testid={testId}
      data-open={open ? "true" : "false"}
      className={`rounded-control border border-line bg-surface ${className}`}
    >
      <button
        type="button"
        data-testid={toggleTestId}
        aria-expanded={open}
        onClick={() => setOpen((was) => !was)}
        className="flex w-full items-center justify-between gap-3 px-3 py-2.5 text-start transition-colors hover:bg-sunk"
      >
        <span className="min-w-0">
          <span className="block text-sm font-medium">{title}</span>
          {summary && (
            <span className="mt-0.5 block truncate text-xs text-muted" dir="auto">
              {summary}
            </span>
          )}
        </span>
        <svg
          viewBox="0 0 24 24"
          className={`size-4 shrink-0 text-muted transition-transform ${open ? "rotate-180" : ""}`}
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="m6 9 6 6 6-6" />
        </svg>
      </button>
      {open && <div className="border-t border-line px-3 pb-3">{children}</div>}
    </div>
  );
}
